
// LegalContent.js
//
// Generic two-column template for legal documents (Privacy, Terms, etc.).
// The document itself (the list of sections — id, title, body) is NOT
// hard-coded here. Each page (Privacy.js, Terms.js, ...) owns its own
// `sections` array and passes it in as a prop:
//
//   const sections = [
//       { id: 'introduction', title: 'I. Introduction', content: <p>...</p> },
//       ...
//   ];
//
//   <LegalContent sections={sections}/>
//
// This component just renders the table-of-contents "bookmarks" on the
// left, the section content on the right, and keeps track of which
// section is currently in view (scroll-spy) to highlight the active
// bookmark and give the user a visual indicator of where they are.

import React, { useEffect, useRef, useState } from 'react';

import TitleField from '../text/TitleField';

import '../../styles/legalcomponents/LegalContent.css';

const LegalContent = ({ sections = [] }) => {

    const [activeId, setActiveId] = useState(sections[0]?.id ?? null);
    const sectionRefs = useRef({});

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id);
                    }
                });
            },
            // Treat a section as "active" once it crosses a line near the
            // top of the viewport, and stop tracking it well before it
            // reaches the bottom — this is what gives the scroll-spy its
            // natural feel.
            { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
        );

        sections.forEach(({ id }) => {
            const el = sectionRefs.current[id];
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [sections]);

    const handleTocClick = (event, id) => {
        event.preventDefault();
        sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="legal-content-container">

            <nav className="legal-table-of-contents-container" aria-label="Table of contents">
                <div className="legal-table-of-contents-title">
                    <TitleField title="Table of Contents" />
                </div>

                <span className="legal-table-of-contents-horizontal-line"></span>

                <ul className="legal-table-contents">
                    {sections.map((section) => (
                        <li key={section.id}>
                            <a
                                href={`#${section.id}`}
                                className={`legal-toc-link ${activeId === section.id ? 'legal-toc-link--active' : ''}`}
                                onClick={(event) => handleTocClick(event, section.id)}
                                aria-current={activeId === section.id ? 'true' : undefined}
                            >
                                {section.title}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>

            <div className="legal-content-info-container">
                {sections.map((section) => (
                    <section
                        key={section.id}
                        id={section.id}
                        ref={(el) => { sectionRefs.current[section.id] = el; }}
                        className="legal-content-section"
                    >
                        <TitleField title={section.title} bold={true} fontSize="1.6rem" />

                        <div className="legal-content-section-body">
                            {section.content}
                        </div>
                    </section>
                ))}
            </div>

        </div>
    );
};


export default LegalContent;
